/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { includedFeatures } from '../plan-v2/seat-unlocks';
import type { PlanFeatureType } from '@/features/pricing';
import type { TeamSeatBlock } from '@/services/requests/housien-services/AgenciesService.team';

interface Props {
  /** The seat blocks the subscription bought, one per plan. */
  blocks: TeamSeatBlock[];
  /** Resolves a catalogue plan from the id a seat block carries. */
  planFor: (planId: string) => PlanFeatureType | null;
}

/**
 * What each plan on this subscription includes, folded away until asked for.
 *
 * The owner picks which seat is theirs before they have used the product,
 * from three names that mean nothing yet. The names alone were the whole
 * question; this is the answer, one click away, without leaving the screen
 * that asked.
 *
 * @param props - The seat blocks and the catalogue lookup.
 * @returns The comparison, collapsed by default.
 */
export function SeatPlanCompare({ blocks, planFor }: Props) {
  const { t } = useTranslation('@pages/onboarding');
  const [comparing, setComparing] = useState(false);

  if (blocks.length === 0) return null;

  /* Relative to the cheapest plan on screen, so "4x mais" means something. */
  const entryInteractions = Math.min(
    ...blocks.map((block) => planFor(block.plan_id)?.limits?.seat?.interactions ?? Infinity),
  );

  const messagesLine = (plan: PlanFeatureType): string => {
    const mine = plan.limits?.seat?.interactions ?? null;
    if (mine == null) return '—';
    return mine > entryInteractions && Number.isFinite(entryInteractions)
      ? t('inviteTeam.compareMessagesTimes', { times: Math.round(mine / entryInteractions) })
      : t('inviteTeam.compareMessagesBase');
  };

  return (
    <>
      <UnstyledButton
        onClick={() => setComparing((open) => !open)}
        aria-expanded={comparing}
        data-testid="seat-plan-compare-toggle"
        style={{
          alignSelf: 'center',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1-5)',
          background: 'none',
          cursor: 'pointer',
          fontSize: 'var(--text-small)',
          fontWeight: 'var(--font-bold)',
          color: 'var(--seed-base)',
        }}
      >
        {t('inviteTeam.ownerPickCompare')}
        <svg
          width={14} height={14} viewBox="0 0 16 16" fill="none"
          stroke="currentColor" strokeWidth={2} strokeLinecap="round"
          strokeLinejoin="round" aria-hidden
          style={{
            transform: comparing ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform var(--duration-medium) var(--ease-in-out)',
          }}
        >
          <path d="M3 6l5 5 5-5" />
        </svg>
      </UnstyledButton>
      <div
        data-testid="seat-plan-compare"
        style={{
          display: 'grid',
          gridTemplateRows: comparing ? '1fr' : '0fr',
          transition: 'grid-template-rows var(--duration-medium) var(--ease-in-out)',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          {/* Flex, not grid: when the tier count is odd, the straggler
              stretches across the leftover row instead of leaving a hole. */}
          <div
            style={{
              display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)',
              paddingTop: 'var(--space-1)',
            }}
          >
            {blocks.map((block, index, all) => {
              const plan = planFor(block.plan_id);
              if (!plan) return null;
              return (
                <PlanColumn
                  key={block.plan_id}
                  plan={plan}
                  messages={messagesLine(plan)}
                  stretched={all.length % 2 === 1 && index === all.length - 1}
                />
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * One plan's column: what a seat on it carries.
 *
 * @param props - The plan, its messages line and whether it fills the row.
 * @returns The column.
 */
function PlanColumn(
  { plan, messages, stretched }: { plan: PlanFeatureType; messages: string; stretched: boolean },
) {
  const { t } = useTranslation('@pages/onboarding');

  return (
    <div
      style={{
        flex: '1 1 40%', minWidth: 0,
        display: 'flex', flexDirection: 'column', gap: 'var(--space-1-5)',
        padding: 'var(--space-3)', borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-ultra-subtle)',
        background: 'var(--surface-raised)',
        // The straggler of an odd count stretches across the whole row;
        // left-aligned text on a full-width card reads lopsided.
        textAlign: stretched ? 'center' : 'left',
        alignItems: stretched ? 'center' : undefined,
      }}
    >
      <Text
        component="span"
        style={{ fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)', color: 'var(--text-primary)' }}
      >
        {plan.title}
      </Text>
      <Text component="span" style={{ fontSize: 'var(--text-tag)', color: 'var(--text-secondary)' }}>
        {messages}
      </Text>
      <Text component="span" style={{ fontSize: 'var(--text-tag)', color: 'var(--text-secondary)' }}>
        {t('features/subscription:v2.ladder.searchesPerMonth')}
        {': '}
        {plan.limits?.seat?.partnership_searches ?? '—'}
      </Text>
      {includedFeatures(
        Object.entries(plan.features).map(([key, on]) => ({ key, tier: on ? 'on' : 'none' })),
      ).map((row) => (
        <Text
          key={row}
          component="span"
          style={{ fontSize: 'var(--text-tag)', color: 'var(--text-primary)', lineHeight: 1.35 }}
        >
          {'✓ '}
          {t(`planChoice.matrix.${row}`)}
        </Text>
      ))}
    </div>
  );
}
